/**
 * Browser / Tauri connectivity → TanStack `onlineManager`, and the replay that
 * follows reconnect.
 *
 * TanStack's default listener already watches `window` online/offline, but two
 * things make an explicit bridge worth having. First, `navigator.onLine` is
 * only ever a lower bound — it reports "online" for a machine attached to a
 * captive portal or a VPN that is up but routing nowhere — so a failed request
 * has to be able to demote us. Second, the desktop shell is a WebView whose
 * online/offline events fire inconsistently when the host machine sleeps and
 * wakes, which is exactly the laptop-in-a-bag case this feature is for; polling
 * on focus and visibility covers what the events miss.
 */
import type { QueryClient } from '@tanstack/react-query'
import { useAuth } from '@/store/auth.store'
import { onlineManager } from '@tanstack/react-query'

import { offlineLedger, restoreOfflineLedger } from './ledger'
import { registerOfflineMutationDefaults } from './mutation-defaults'
import { selectOwnerWrites, selectPendingWrites } from './offline-queue'
import { retryOfflineWrite } from './queue-actions'

function readBrowserOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

let resumeChain: Promise<unknown> = Promise.resolve()

/**
 * Replay whatever is parked. Serialised against itself because reconnect
 * signals arrive in bursts — an `online` event, a window focus and a
 * visibilitychange can all land within the same tick after a laptop wakes.
 */
export function resumeOfflineWrites(queryClient: QueryClient): Promise<void> {
  resumeChain = resumeChain
    .then(async () => {
      await restoreOfflineLedger()
      await queryClient.resumePausedMutations()
      // The durable outbox is independent of read-cache expiry and cache clears.
      // Only provably unsent work can be rebuilt automatically.
      for (const entry of selectOwnerWrites(
        offlineLedger.list(),
        useAuth.getState().user?.id ?? null,
      )) {
        if (entry.status === 'queued' && entry.attempts === 0) {
          await retryOfflineWrite(queryClient, offlineLedger, entry)
        }
      }
    })
    .then(() => {
      offlineLedger.prune()
    })
    .catch((error: unknown) => {
      // Individual failures are already recorded on their ledger rows and
      // surfaced in the pending-writes popover; this only guards the chain.
      console.warn('[OfflineQueue] Replay finished with errors', error)
    })

  return resumeChain as Promise<void>
}

/**
 * Wire everything up. Call once, as early as possible — the order matters:
 *
 *   1. mutation defaults, so restored mutations have a `mutationFn` to run
 *   2. the ledger, so a replay can recognise a write it already sent
 *   3. `onlineManager`, so TanStack pauses instead of failing
 *   4. only then resume
 */
export function initializeOfflineQueue(queryClient: QueryClient): () => void {
  registerOfflineMutationDefaults(queryClient)
  void restoreOfflineLedger().catch(error =>
    console.warn('[OfflineQueue] Restore failed', error),
  )

  onlineManager.setEventListener(setOnline => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    // A wake or a tab-return is the moment `navigator.onLine` is most likely to
    // have changed without an event having fired.
    const handleRecheck = () => setOnline(readBrowserOnline())

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('focus', handleRecheck)
    document.addEventListener('visibilitychange', handleRecheck)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('focus', handleRecheck)
      document.removeEventListener('visibilitychange', handleRecheck)
    }
  })

  onlineManager.setOnline(readBrowserOnline())

  // App start is a resume trigger in its own right: writes captured in a
  // previous session come back as paused mutations and would otherwise sit
  // there until the next connectivity *transition*, which never arrives if the
  // machine was already online at launch.
  if (onlineManager.isOnline()) {
    void resumeOfflineWrites(queryClient)
  }

  const unsubscribeOnline = onlineManager.subscribe(online => {
    if (online) void resumeOfflineWrites(queryClient)
  })

  return unsubscribeOnline
}

/** Count of writes waiting or blocked — what the status chip shows. */
export function getPendingWriteCount(): number {
  return selectPendingWrites(
    selectOwnerWrites(
      offlineLedger.list(),
      useAuth.getState().user?.id ?? null,
    ),
  ).length
}
